import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Scissors, Lock, AlertTriangle, Check } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing or invalid reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      console.error("Reset password failed:", err);
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : !err.response
          ? "Network issue. Check your connection and try again."
          : "Couldn't reset password. The link may be expired — request a new one.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 animate-fade-in">
      <div className="w-full max-w-md cm-card p-8 md:p-10">
        <div className="flex items-center gap-2 mb-2">
          <Scissors className="w-5 h-5 text-copper" />
          <span className="cm-heading text-xl">SET NEW PASSWORD</span>
        </div>
        <p className="text-sm text-gray-400 mb-8">
          Pick a new password — at least 6 characters.
        </p>

        {done ? (
          <div className="cm-card p-6 border-copper/50" data-testid="reset-success-banner">
            <div className="flex items-start gap-2 text-copper">
              <Check className="w-5 h-5 mt-0.5" />
              <p className="text-sm leading-relaxed">
                Password updated. Redirecting you to sign in…
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="reset-password" className="cm-label block mb-2">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="cm-input pl-10"
                  placeholder="6+ characters"
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <div>
              <label htmlFor="reset-confirm" className="cm-label block mb-2">Confirm new password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
                <input
                  id="reset-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="cm-input pl-10"
                  placeholder="Repeat password"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>
            {error && (
              <div role="alert" aria-live="assertive" className="flex items-start gap-2 text-sm text-red-400" data-testid="reset-error">
                <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden="true" /> {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !token}
              className="cm-btn-primary w-full"
              data-testid="reset-submit-button"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-gray-400 text-center">
          <Link to="/login" className="text-copper hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
